import type {MouseEventHandler, ReactElement, ReactNode} from 'react';

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export enum ButtonVariant {
  DEFAULT = 'default',
  PREMIUM = 'premium',
  PREMIUM_PLUS = 'premium-plus',
  AI = 'ai',
  ERROR = 'error',
  SUCCESS = 'success',
  DASHED = 'dashed',
}

/**
 * Button looks kept from before the type + variant API. Rendered through their own global `.btn` class.
 */
export enum LegacyButtonType {
  PREMIUM3 = 'premium3',
}

export enum ButtonSize {
  SMALL = 'sm',
  MEDIUM = 'md',
  LARGE = 'lg',
  EXTRA_LARGE = 'xl',
}

/**
 * The global `.btn` colour class is `<type>`, `<type>-<variant>` or `<type>-on-dark`, and the size class is the `ButtonSize` value.
 */
export const ON_DARK_BUTTON_CLASS_SUFFIX = '-on-dark';

/**
 * Variants for every type.
 */
export type SharedButtonVariant = ButtonVariant.DEFAULT | ButtonVariant.AI | ButtonVariant.ERROR;

/**
 * Variants for the primary type only.
 */
export const PRIMARY_ONLY_BUTTON_VARIANTS = [ButtonVariant.PREMIUM, ButtonVariant.PREMIUM_PLUS, ButtonVariant.SUCCESS, ButtonVariant.DASHED] as const;

export type PrimaryOnlyButtonVariant = (typeof PRIMARY_ONLY_BUTTON_VARIANTS)[number];

interface SharedVariantButtonAppearance {
  type?: ButtonType;
  variant?: SharedButtonVariant;
  onDark?: false;
}

interface PrimaryOnlyVariantButtonAppearance {
  type?: ButtonType.PRIMARY;
  variant: PrimaryOnlyButtonVariant;
  onDark?: false;
}

/**
 * On-dark exists for the default variant only.
 */
interface OnDarkButtonAppearance {
  type?: ButtonType;
  variant?: ButtonVariant.DEFAULT;
  onDark: true;
}

export interface LegacyButtonAppearance {
  type: LegacyButtonType;
  variant?: never;
  onDark?: never;
}

export type TypeVariantButtonAppearance = SharedVariantButtonAppearance | PrimaryOnlyVariantButtonAppearance | OnDarkButtonAppearance;

export type ButtonAppearance = TypeVariantButtonAppearance | LegacyButtonAppearance;

/**
 * Opts a label out of the button's sentence-case lowercasing, for labels holding a proper noun or an acronym.
 */
export const PROPER_NOUN_BUTTON_CLASS = '-noun';

export const UPSELL_BUTTON_APPEARANCE: ButtonAppearance = {type: ButtonType.PRIMARY, variant: ButtonVariant.PREMIUM_PLUS};

export const PRIMARY_BUTTON_APPEARANCE: ButtonAppearance = {type: ButtonType.PRIMARY};

export const PREMIUM_BUTTON_APPEARANCE: ButtonAppearance = {type: ButtonType.PRIMARY, variant: ButtonVariant.PREMIUM};

export const SUCCESS_BUTTON_APPEARANCE: ButtonAppearance = {type: ButtonType.PRIMARY, variant: ButtonVariant.SUCCESS};

export const ERROR_BUTTON_APPEARANCE: ButtonAppearance = {type: ButtonType.PRIMARY, variant: ButtonVariant.ERROR};

export enum IconPlacement {
  LEFT = '-icon-left',
  RIGHT = '-icon-right',
}

export interface ButtonBaseProps {
  /**
   * ID to give to the button
   */
  id?: string;
  /**
   * Additional classes to give to the button
   */
  customClasses?: string;
  size?: ButtonSize;
  iconPlacement?: IconPlacement;
  href?: string;
  /**
   * The class that renders the icon
   */
  icon?: string;
  iconClassName?: string;
  /**
   * Additional classes to give to the text span too
   */
  textClasses?: string;
  /**
   * Text on button to display
   */
  text?: string;
  title?: string;
  /**
   * Text on button to below 500px width
   */
  shortText?: string;
  /**
   * Handler for when mouse is clicked
   */
  onClick?: MouseEventHandler | (() => Promise<unknown>);
  disabled?: boolean;
  /**
   * a key value pair of any attributes we want to give to the root component such as data-id etc
   */
  attributes?: Record<string, string>;
  children?: ReactNode;
  isLoading?: boolean;
  isFullWidth?: boolean;
  keepsWidthWhileLoading?: boolean;
  /**
   * classes that get applied to the loader
   */
  loaderClassName?: string;
  isActive?: boolean;
  /**
   * Set this to enable click tracking for this button on GA4.
   */
  ga4ClickName?: string;
  customIcon?: ReactElement;
  customText?: ReactElement;
  loadingStateText?: string;
  showUpsell?: boolean;
  'data-testid'?: string;
  dangerouslySetTextAsHTML?: boolean;
  isSelected?: boolean;
}

export type ButtonProps = ButtonBaseProps & ButtonAppearance;

/**
 * `Omit` that keeps `ButtonProps` a union, so the type + variant rules still apply to the result.
 */
export type ButtonPropsWithout<Key extends keyof ButtonBaseProps> = DistributiveOmit<ButtonProps, Key>;

type DistributiveOmit<Props, Key extends PropertyKey> = Props extends unknown ? Omit<Props, Key> : never;
